import React from 'react';
import {
    FormGroup,
    Row,
    Col,
    Progress,
    Collapse,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faWarehouse, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    FormDivider,
    FormGroupColumn,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    SubHeading,
    ToastMessage,
    SimpleTable,
    FormCheckbox,
    Input,
    NumericInput,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext, {
    ApiRoutes, AppNavPaths, ChargeTypeResourceTypes, ChargeTypeUnits,
} from '../Common';
import { handleFormSaveError } from '../common/forms/ValidationError';
import ChargeTypeSelect from '../chargeType/ChargeTypeSelect';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { NotAuthorizedPage } from '../status/StatusCodes';
import { TipAlert } from '../common/TipAlert';
import { SaveButton } from '../common/forms/SaveButton';
import { ChargeTypeGroup } from './ChargeTypeGroup';
import './ChargeTypeGroupForm.scss';

class ChargeTypeGroupForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.chargeTypeSelectorRef = React.createRef();

        const stateBase = {
            group: new ChargeTypeGroup(),
            chargeTypeGroupStatuses: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onRentalGroupChanged = this.onRentalGroupChanged.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    // #region CHARGE TYPES
    onAddChargeTypes = () => {
        const { group } = { ...this.state };
        const { chargeTypes } = { ...group };
        const typesToOmit = (chargeTypes ?? []).map((c) => c.chargeTypeId) ?? [];
        // pass in types already selected
        this.chargeTypeSelectorRef.current.open(null, [...typesToOmit]);
    };

    onAddChargeTypesCallback = (selections) => {
        const { group } = { ...this.state };
        let updated = [...group.chargeTypes] ?? [];
        const isRentalGroup = group.isRental ?? false;
        // get selections from the popup
        const selectedChargeTypes = [...selections].map((x) => ({
            chargeTypeGroupId: 0,
            chargeTypeId: x.id,
            description: x.description,
            groups: x.appliesToGroup,
            employees: x.appliesToUsers,
            equipment: x.appliesToEquipment,
            quantity: 1,
            billingRateTypeId: 2,
            resourceTypeId: x.resourceTypeId,
        }));

        const hasRentalCharges = selectedChargeTypes.some((ct) => ct.resourceTypeId === ChargeTypeResourceTypes.Rental);
        let chargeTypesToAdd = selectedChargeTypes;
        if (!isRentalGroup && hasRentalCharges) {
            const rentalCount = selectedChargeTypes.filter((ct) => ct.resourceTypeId === ChargeTypeResourceTypes.Rental).length;
            chargeTypesToAdd = selectedChargeTypes.filter((ct) => ct.resourceTypeId !== ChargeTypeResourceTypes.Rental);
            toast.warning(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Some Charges not Added"
                    message={`${rentalCount} rental charge${rentalCount > 1 ? 's' : ''} ${rentalCount > 1 ? 'were' : 'was'} not added because this is not a rental group. ${chargeTypesToAdd.length > 0 ? 'The remaining non-rental charges were added.' : ''}`}
                />,
            );
        }
        // merge additions with current charges
        updated = updated.concat([...chargeTypesToAdd]);

        group.chargeTypes = [...updated];

        this.setState({ group: { ...group } });
    };

    onChange = onFieldChange;

    onClearErrors = () => this.setState({ errors: {} });

    onClose = (response) => {
        this.resetForm();
        this.context.setFormOpened(false);
        this.props.onClose(response);
    };

    onDelete = async () => {
        const { group } = this.state;
        const response = await httpClient.delete(ApiRoutes.chargeTypeGroup.delete(group.id)).catch(this.onCatchFetchError);
        if (response) this.onClose(response);
    };

    onRemoveChargeType = (chargeTypeIndex) => {
        const { group } = { ...this.state };
        const chargeTypes = [...group.chargeTypes];
        chargeTypes.splice(chargeTypeIndex, 1);
        group.chargeTypes = [...chargeTypes];
        this.setState({ group: { ...group } });
    };

    onSelectChanged = onReactSelectChanged;

    onRentalGroupChanged = (isChecked) => {
        const { group } = { ...this.state };
        let updatedChargeTypes = [...(group.chargeTypes ?? [])];

        if (!isChecked) {
            const rentalCount = updatedChargeTypes.filter((ct) => ct.resourceTypeId === ChargeTypeResourceTypes.Rental).length;

            if (rentalCount > 0) {
                updatedChargeTypes = updatedChargeTypes.filter((ct) => ct.resourceTypeId !== ChargeTypeResourceTypes.Rental);

                toast.warning(
                    <ToastMessage
                        icon={faCheckCircle}
                        header="Some Charges Removed"
                        message={`${rentalCount} rental charge${rentalCount > 1 ? 's' : ''} ${rentalCount > 1 ? 'were' : 'was'} removed because this is no longer a rental group.`}
                    />,
                );
            }
        }

        const updatedGroup = {
            ...group,
            isRental: isChecked,
            isLumpSum: isChecked ? group.isLumpSum : false,
            chargeTypes: updatedChargeTypes,
        };

        this.setState({ group: updatedGroup });
    };

    onSubmit = async () => {
        // Clear any fluent api errors
        this.onClearErrors();
        this.setSaving(true);

        const { group } = this.state;

        const cloned = clone(group);

        this.setState({ originalData: cloned });

        if (this.props.location.pathname == AppNavPaths.ChargeTypeGroupNew) {
            await this.createGroup(cloned);
        } else {
            await this.updateGroup(cloned);
        }
    };

    setSaving = (b) => this.setState({ saving: b });

    createGroup = async (group) => {
        const response = await httpClient.post(ApiRoutes.chargeTypeGroup.create(), group)
            .catch(this.handleSaveError);

        if (response && !isNaN(response)) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message="Saved."
                />,
            );

            this.context.history.push(`${AppNavPaths.ChargeTypeGroup}/${response}`);
        }

        this.setSaving(false);
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    async populateState() {
        const groupId = this.props.match.params.id;

        const [group, billingCycles, chargeTypeGroupStatuses, unitsRaw] = await Promise.all([
            groupId ? httpClient.js(ApiRoutes.chargeTypeGroup.byId(groupId)) : new ChargeTypeGroup(),
            httpClient.js(ApiRoutes.typeAheads.billingCycles())
                .catch(() => console.log('Unable to get billing cycles.')),
            httpClient
                .js(ApiRoutes.typeAheads.chargeTypeGroupStatuses())
                .catch(() => console.log('Unable to get charge type group statuses.')),
           httpClient.js(ApiRoutes.chargeTypeUnit.all()),
        ]);

        const originalData = clone(group);

        // Inventory items
        const { tenantSettings } = this.context.tenant;

        const [inventoryItems] = await Promise.all([
            httpClient.js(ApiRoutes.typeAheads.quickBooksInventoryItems()),
        ]);
        const billingRates = {
            OT: { label: 'OT', value: 1 },
            REG: { label: 'Regular', value: 2 },
            DT: { label: 'DT', value: 3 },
            RSD: { label: 'RSD', value: 4 },
        };
        const billingRateTypes = [{ label: 'OT', value: 1 }, { label: 'Regular', value: 2 }];
        const employeeBillingRateTypes = [billingRates.OT, billingRates.REG];
        if (tenantSettings.enableDoubleTimeBilling) {
            employeeBillingRateTypes.push(billingRates.DT);
        }
        if (tenantSettings.enableShiftDifferentialBilling) {
            employeeBillingRateTypes.push(billingRates.RSD);
        }
        const units = unitsRaw.filter((unit) => unit.value !== ChargeTypeUnits.Percentage);

        this.setState({
            originalData,
            group,
            loading: false,
            saving: false,
            associateInventoryItem: tenantSettings.useQuickBooksInvoicing,
            inventoryItems,
            billingRateTypes,
            employeeBillingRateTypes,
            billingCycles,
            chargeTypeGroupStatuses,
            units,
        });
    }

    resetForm = () => this.setState((prevState) => ({ formValidated: false, originalData: clone(prevState.group) }));

    updateGroup = async (group) => {
        const response = await httpClient.put(ApiRoutes.chargeTypeGroup.byId(group.id), group)
            .catch(this.handleSaveError);

        if (response && parseInt(response.id ?? 0) >= 0) {
            toast.success(
                <ToastMessage
                    icon={faCheckCircle}
                    header="Save Successful"
                    message={`[${response.invoiceDescription}] saved.`}
                />,
            );

            this.context.history.push(`${AppNavPaths.ChargeTypeGroup}/${response.id}`);
        }

        this.setSaving(false);
    };

    getChargeTypeTableHeaders = () => {
        const { group } = this.state;
        const { hasTotalCharges, isRental } = group;

        if (hasTotalCharges) {
            return [
                { name: 'Description' },
                { name: 'Equipment' },
                { name: 'Rate Type' },
                { name: '', class: 'text-center' },
            ];
        }
        if (isRental) {
            return [
                { name: 'Description' },
                { name: 'Equipment' },
                { name: 'Max Quantity' },
                { name: 'Rate Type' },
                { name: '', class: 'text-center' },
            ];
        }

        return [
            { name: 'Description' },
            { name: 'Equipment' },
            { name: 'Quantity' },
            { name: 'Rate Type' },
            { name: '', class: 'text-center' },
        ];
    };

    renderGroupChargeTypesRows = () => {
        const { billingRateTypes, group, employeeBillingRateTypes } = { ...this.state };
        const { chargeTypes, hasTotalCharges } = group;
        if (!chargeTypes) return '';
        return chargeTypes.map((chargeType, chargeTypeIndex) => {
            let billingRateTypeOptions = billingRateTypes;
            if (chargeType.employees != '' && employeeBillingRateTypes?.length) {
                billingRateTypeOptions = employeeBillingRateTypes;
            }
            return (
                <tr
                    key={chargeTypeIndex}
                    style={{ maxHeight: '10px' }}
                >
                    <td style={{ verticalAlign: 'middle' }}>
                        {chargeType.description}
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>{chargeType.equipment}</td>
                    {!hasTotalCharges && (
                        <td
                            className="text-center"
                            width="100"
                        >
                            <div style={{ marginTop: '12px' }}>
                                <NumericInput
                                    className="text-right"
                                    name={`quantity${chargeTypeIndex}`}
                                    required
                                    value={chargeType.quantity ?? ''}
                                    onChange={(event) => {
                                        const val = event.target.value; const
                                            { group } = { ...this.state };
                                        group.chargeTypes[chargeTypeIndex].quantity = val;
                                        this.setState({ group });
                                    }}
                                    min={0}
                                    step={0.01}
                                    wholeNumber
                                    preventNegative
                                    error="Charge Quantity is required"
                                />
                            </div>
                        </td>
                    )}

                    <td
                        style={{ verticalAlign: 'middle' }}
                        width="150"
                    >
                        <div style={{ marginTop: '12px' }}>
                            <ValidatedSelect
                                name={`billingRateTypeId${chargeTypeIndex}`}
                                options={billingRateTypeOptions}
                                hideClear
                                value={(billingRateTypeOptions ?? []).find((s) => s.value == chargeType.billingRateTypeId) ?? ''}
                                onChange={(event) => {
                                    const val = event.value; const
                                        { group } = { ...this.state };
                                    group.chargeTypes[chargeTypeIndex].billingRateTypeId = val;
                                    this.setState({ group });
                                }}
                            />
                        </div>
                    </td>
                    <td
                        style={{ verticalAlign: 'middle' }}
                        className="text-center"
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            title={`Remove ${chargeType.description}`}
                            className="text-danger cursor-pointer"
                            onClick={() => this.onRemoveChargeType(chargeTypeIndex)}
                        />
                    </td>
                </tr>
            );
        });
    };

    render() {
        return (
            <CommonContext.Consumer>
                {(value) => {
                    const { tenantSettings } = (value ?? {}).tenant ?? {};

                    if (!tenantSettings) return (<Progress />);

                    const {
                        group,
                        originalData,
                        errors,
                        formValidated,
                        saving,
                        billingCycles,
                        chargeTypeGroupStatuses,
                        units,
                    } = this.state;
                    const inventoryItems = (this.state.inventoryItems ?? []);
                    const existing = !!group.id;

                    const showConfirmationPrompt = (!isEqual(originalData, group));
                    const invoicingEnabled = !!tenantSettings.invoicingEnabled;
                    const chargeTypeGroupsEnabled = !!tenantSettings.chargeTypeGroupsEnabled;
                    const { chargeTypes } = group;
                    const allChargesHourly =
                        chargeTypes.length > 0 &&
                        chargeTypes.every((ct) => ct.unitsId === 1); // Hourly charges
                    return (
                        <>
                            <FormPrompt
                                when={!!showConfirmationPrompt}
                                message="You have unsaved changes, are you sure you want to leave?"
                            />

                            {
                                !invoicingEnabled || !chargeTypeGroupsEnabled ? <NotAuthorizedPage />
                                    : (
                                        <>
                                            <AppPageForm
                                                formShown={this.context.formIsOpen}
                                                formId="groupForm"
                                                formHeadingIcon={faWarehouse}
                                                formHeading={!group.id ? 'New Charge Type Group' : 'Edit Charge Type Group'}
                                                formName="groupForm"
                                                formRef={this.formRef}
                                                onSubmit={this.onSubmit}
                                                setIsValidated={(value) => {
                                                    this.setState({ formValidated: value });
                                                }}
                                                isValidated={formValidated}
                                                saving={saving}
                                                errors={errors}
                                                onClearErrors={this.onClearErrors}
                                                loading={this.state.loading}
                                                backUrl={AppNavPaths.ChargeTypeGroups}
                                                backLabel="Back to Charge Type Groups"
                                            >
                                                <GroupedRow>
                                                    <FormGroupColumn>
                                                        <Input
                                                            label="Invoice Description"
                                                            id="invoiceDescription"
                                                            name="group.invoiceDescription"
                                                            value={group.invoiceDescription ?? ''}
                                                            onChange={this.onChange}
                                                            placeholder="Enter Invoice Description (max 150 characters)"
                                                            maxLength="150"
                                                            pattern="[^()/><\][\\\x22,;|]+"
                                                            required
                                                            error="Invoice Description is required and can only contain the following special characters: hyphens and periods."
                                                        />
                                                        <NumericInput
                                                            label="Bill Rate"
                                                            id="billRate"
                                                            name="group.billRate"
                                                            value={group.billRate ?? ''}
                                                            onChange={this.onChange}
                                                            placeholder="Enter Bill Rate"
                                                            min={0}
                                                            step={0.001}
                                                            required
                                                            error="A flat charge is required"
                                                            fractionDigits={3}
                                                        />
                                                        <GroupedRow>
                                                            <FormGroupColumn>
                                                                <NumericInput
                                                                    label="Bill Quantity"
                                                                    id="billQuantity"
                                                                    name="group.billQuantity"
                                                                    value={group.billQuantity ?? ''}
                                                                    onChange={this.onChange}
                                                                    placeholder="Enter Hours"
                                                                    step=".25"
                                                                    required
                                                                    error="The quantity in hours is required."
                                                                />
                                                            </FormGroupColumn>
                                                            <FormGroupColumn>
                                                                <ValidatedSelect
                                                                    id="billUnitId"
                                                                    name="group.billUnitId"
                                                                    label="Bill Unit"
                                                                    required
                                                                    options={units}
                                                                    value={
                                                                        (units ?? []).find(
                                                                            (s) => s.value === group.billUnitId,
                                                                        ) ?? ''
                                                                    }
                                                                    onChange={
                                                                        this.onSelectChanged
                                                                    }
                                                                    error="A units selection is required."
                                                                />
                                                            </FormGroupColumn>
                                                        </GroupedRow>

                                                        {this.state.associateInventoryItem && inventoryItems.length > 0 && (
                                                            <ValidatedSelect
                                                                id="quickBooksInventoryItemId"
                                                                name="group.quickBooksInventoryItemId"
                                                                label="Inventory Item"
                                                                options={inventoryItems}
                                                                value={(inventoryItems ?? []).find((s) => s.value == group.quickBooksInventoryItemId) ?? ''}
                                                                onChange={this.onSelectChanged}
                                                            />
                                                        )}
                                                        <ValidatedSelect
                                                            id="chargeTypeGroupStatusId"
                                                            name="group.chargeTypeGroupStatusId"
                                                            label="Charge Type Group Status"
                                                            options={chargeTypeGroupStatuses}
                                                            required
                                                            value={
                                                                (chargeTypeGroupStatuses ?? []).find(
                                                                    (x) => group.chargeTypeGroupStatusId
                                                                        === x.value,
                                                                ) ?? ''
                                                            }
                                                            onChange={this.onSelectChanged}
                                                            error="Charge Type group status selection is required."
                                                        />
                                                        <FormGroup>
                                                            <FormCheckbox
                                                                id="autoCalculate"
                                                                checked={group.autoCalculate}
                                                                onChange={(e) => {
                                                                    group.autoCalculate = e.target.checked;
                                                                    this.setState({ group });
                                                                }}
                                                                labelText="Auto Generate"
                                                            />
                                                        </FormGroup>
                                                        {Boolean(allChargesHourly) && (
                                                            <FormGroup>
                                                                <FormCheckbox
                                                                    id="billAsDailyRate"
                                                                    checked={group.billAsDailyRate}
                                                                    onChange={(e) => {
                                                                        group.billAsDailyRate = e.target.checked;
                                                                        this.setState({ group });
                                                                    }}
                                                                    labelText="Bill as Daily Rate"
                                                                />
                                                            </FormGroup>
                                                        )}
                                                        <FormGroup>
                                                            <FormCheckbox
                                                                id="isRental"
                                                                checked={group.isRental}
                                                                onChange={(e) => this.onRentalGroupChanged(e.target.checked)}
                                                                labelText="Is a Rental Charge Type Group"
                                                            />
                                                        </FormGroup>
                                                        <Collapse isOpen={group.isRental}>
                                                            <ValidatedSelect
                                                                id="billingCycle"
                                                                name="group.billingCycleId"
                                                                label="Rental Period"
                                                                required={group.isRental}
                                                                options={billingCycles}
                                                                value={
                                                                    (
                                                                        billingCycles ?? []
                                                                    ).find(
                                                                        (x) => group.billingCycleId
                                                                            === x.value,
                                                                    ) ?? ''
                                                                }
                                                                onChange={
                                                                    this.onSelectChanged
                                                                }
                                                                error="Rental Period selection is required."
                                                            />
                                                            <FormGroup>
                                                                <FormCheckbox
                                                                    id="isLumpSum"
                                                                    checked={group.isLumpSum}
                                                                    onChange={(e) => {
                                                                        group.isLumpSum = e.target.checked;
                                                                        this.setState({ group });
                                                                    }}
                                                                    labelText="Bill Once per Project"
                                                                />
                                                            </FormGroup>
                                                        </Collapse>
                                                        <FormGroup>
                                                            <FormCheckbox
                                                                id="hasTotalCharges"
                                                                checked={group.hasTotalCharges}
                                                                onChange={(e) => {
                                                                    const isChecked = e.target.checked;
                                                                    const updatedGroup = {
                                                                        ...group,
                                                                        hasTotalCharges: isChecked,
                                                                        numTotalCharges: isChecked ? group.numTotalCharges : null,
                                                                    };
                                                                    this.setState({ group: updatedGroup });
                                                                }}
                                                                labelText="Enable Charge Type Pooling"
                                                            />
                                                        </FormGroup>
                                                        <Collapse isOpen={group.hasTotalCharges}>
                                                            <TipAlert
                                                                label="This charge type group can be filled with any combination of charge types, up to the total number specified below."
                                                            />

                                                            <NumericInput
                                                                label="Total Number of Charges"
                                                                id="numTotalCharges"
                                                                name="group.numTotalCharges"
                                                                value={group.numTotalCharges ?? ''}
                                                                onChange={this.onChange}
                                                                step="1"
                                                                min={1}
                                                                wholeNumber
                                                                required={group.hasTotalCharges}
                                                                error="You must enter a total number of charges to use Charge Type Pooling"
                                                            />
                                                        </Collapse>
                                                        <FormGroup>
                                                            <FormCheckbox
                                                                id="expandOnCrossDispatch"
                                                                checked={group.expandOnCrossDispatch}
                                                                onChange={(e) => {
                                                                    group.expandOnCrossDispatch = e.target.checked;
                                                                    this.setState({ group });
                                                                }}
                                                                labelText="Expand on Invoice When Cross-Dispatched Charges Exist"
                                                            />
                                                        </FormGroup>
                                                    </FormGroupColumn>

                                                </GroupedRow>
                                                <Row>
                                                    <Col>
                                                        <SubHeading>Charge Types</SubHeading>
                                                        <SimpleTable
                                                            className="mb-0"
                                                            id="chargeTypesTable"
                                                            onAddFunction={this.onAddChargeTypes}
                                                            addButtonEnabled
                                                            addLabelText={this.context.formIsOpen ? (
                                                                <span>
                                                                    <i className="fa fa-spin fa-circle-notch mr-1" />
                                                                    Opening charge types form...
                                                                </span>
                                                            ) : 'Add Charge Types'}
                                                            noTopBorder
                                                            permission="chargetype"
                                                            tableHeaderLabels={this.getChargeTypeTableHeaders()}
                                                            editable={false}
                                                            entities={(this.state.group ?? {}).chargeTypes ?? []}
                                                            rowRenderer={this.renderGroupChargeTypesRows}
                                                            noDataText="No charge types found."
                                                        />
                                                    </Col>
                                                </Row>
                                                <FormDivider />

                                                <FlexCenterRow className="mb-3">
                                                    <SaveButton
                                                        saving={!!this.state.saving}
                                                        label={!existing ? 'Save New Charge Type Group' : 'Save'}
                                                    />
                                                </FlexCenterRow>
                                            </AppPageForm>

                                            <ChargeTypeSelect
                                                ref={this.chargeTypeSelectorRef}
                                                id="chargeTypeSelector"
                                                title="Add Charge Type to Group"
                                                onAddCallback={this.onAddChargeTypesCallback}
                                                billableOnly
                                            />
                                        </>
                                    )

                            }
                        </>
                    );
                }}
            </CommonContext.Consumer>
        );
    }
}
export default ChargeTypeGroupForm;
